import { useEffect, useState } from 'react'

export type NoteDraftStatus = 'idle' | 'pending' | 'saved' | 'error' | 'conflict' | 'gone'

export type NoteDraftEntry = {
  revision: number | null
  status: NoteDraftStatus
  deletesOn: Date | null
  acked: string | null
  // When the text on the server was last written, shown as "Saved 14:02".
  savedAt: Date | null
  // The form differs from what was saved and a save is scheduled.
  changed: boolean
  // A save failed and no save has succeeded since. Unlike status, it stays set while a retry is in flight.
  failing: boolean
}

export type NoteDraftSlot = {
  uid: string
  type: string
}

const EMPTY: NoteDraftEntry = {
  revision: null,
  status: 'idle',
  deletesOn: null,
  acked: null,
  savedAt: null,
  changed: false,
  failing: false,
}

const keyOf = ({ uid, type }: NoteDraftSlot) => `${uid}:${type}`

const entries = new Map<string, NoteDraftEntry>()
const entryListeners = new Map<string, Set<() => void>>()

const notify = (listeners: Set<() => void> | undefined) => {
  listeners?.forEach((listener) => listener())
}

export const getNoteDraftEntry = (slot: NoteDraftSlot): NoteDraftEntry => entries.get(keyOf(slot)) ?? EMPTY

export const updateNoteDraftEntry = (slot: NoteDraftSlot, patch: Partial<NoteDraftEntry>) => {
  const key = keyOf(slot)
  const next = { ...getNoteDraftEntry(slot), ...patch }
  if (patch.status === 'error') {
    next.failing = true
  } else if (patch.status === 'saved') {
    next.failing = false
  }
  entries.set(key, next)
  notify(entryListeners.get(key))
}

export const clearNoteDraftEntry = (slot: NoteDraftSlot) => {
  const key = keyOf(slot)
  entries.delete(key)
  notify(entryListeners.get(key))
}

export const useNoteDraftEntry = ({ uid, type }: NoteDraftSlot): NoteDraftEntry => {
  const [entry, setEntry] = useState(() => getNoteDraftEntry({ uid, type }))

  useEffect(() => {
    const slot = { uid, type }
    const key = keyOf(slot)
    const listener = () => setEntry(getNoteDraftEntry(slot))
    const listeners = entryListeners.get(key) ?? new Set<() => void>()
    listeners.add(listener)
    entryListeners.set(key, listeners)
    listener()
    return () => {
      listeners.delete(listener)
    }
  }, [type, uid])

  return entry
}

const hasErrorCode = (error: unknown, code: string): boolean => {
  if (typeof error !== 'object' || error === null || !('graphQLErrors' in error)) {
    return false
  }
  const { graphQLErrors } = error as { graphQLErrors?: { extensions?: { code?: string } }[] }
  return Boolean(graphQLErrors?.some((graphQLError) => graphQLError.extensions?.code === code))
}

export const isNoteDraftConflict = (error: unknown): boolean => hasErrorCode(error, 'NOTE_DRAFT_CONFLICT')

export const isNoteDraftGone = (error: unknown): boolean => hasErrorCode(error, 'NOTE_DRAFT_GONE')

const sortKeys = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(sortKeys)
  }
  if (typeof value === 'object' && value !== null && !(value instanceof Date)) {
    return Object.fromEntries(
      Object.keys(value)
        .sort()
        .map((key) => [key, sortKeys((value as Record<string, unknown>)[key])])
    )
  }
  return value
}

// Postgres jsonb reorders keys, so the saved text is compared with keys sorted.
export const serializeNoteDraft = (value: unknown): string => JSON.stringify(sortKeys(value))

export const resetNoteDraftSessionForTests = () => {
  entries.clear()
  entryListeners.clear()
}
